import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { BottomSheetComponent, ListItem } from '@material-library';
import { Store, select } from '@ngrx/store';
import { debounceTime, Subscription } from 'rxjs';
import { ShareService } from '../../services/share.service';
import * as fromRoot from '../../../reducers';
import { BlendEvent, PermissionActions, User } from '../../models';
import { BlendService } from '../../services/blend.service';
import { UserService } from '../../../../app/core/user.service';
import { environment } from '../../../../environments/environment';
import { BillingService } from 'src/app/settings/billing/billing.service';
import { BroadcastService } from '../../services/broadcast.service';
import { ChargebeeService } from '../../services/chargebee.service';


@Component({
  selector: 'app-user-panel',
  templateUrl: './user-panel.component.html',
  styleUrls: ['./user-panel.component.scss']
})
export class UserPanelComponent implements OnInit, OnDestroy {

  @Input() bottomSheet: BottomSheetComponent;
  @Input() isPublink?: boolean = false;
  @Output() changeStartPage: EventEmitter<boolean> = new EventEmitter(false)
  @Output() closePanel = new EventEmitter<void>();
  public profileMenuItems: ListItem[] = [];
  public signOutMenuItems: ListItem[] = [];
  private subscription: Subscription;
  public user: User;
  public progressValue: number = 0;
  private defaultLogo: string;
  public avatarUrl: string;
  public avatarEndpoint = `${environment.logohost}avatar/saved/`;
  public planName: string;
  public canManageBilling: boolean = false;
  private themeSubscription: Subscription;
  private broadcastSubscription: Subscription;

  constructor(
    private renderer: Renderer2,
    private shareService: ShareService,
    private router: Router,
    private store: Store<fromRoot.State>,
    private blendService: BlendService,
    private userService: UserService,
    private billingService: BillingService,
    private broadcastService: BroadcastService,
    private chargebeeService: ChargebeeService,

  ) { }

  async ngOnInit() {

    this.broadcastSubscription = this.broadcastService.on('event:keydown:27') //On escape
    .subscribe(() => {
      this.closePanel.emit();
      this.bottomSheet?.dismissBottomSheet?.();
    });

    this.subscription = this.store.pipe(select(fromRoot.getAuthUser))
      .subscribe(async (data) => {
        this.user = data;
        this.planName = this.user.plan_name;
        this.canManageBilling = this.userService.checkPermission(PermissionActions.MANAGE_BILLING);
        this.progressValue = this.user.disk_usage_percent;
        this.defaultLogo = this.user.avatar?.avatar_data;
        this.avatarUrl = this.defaultLogo ? `${this.avatarEndpoint}${this.user.id}` : '';
      });

    this.profileMenuItems =
      [
        {
          primaryText: 'Profile',
          isInteractive: true,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'user'
          },
          onClick: () => {
            this.navigateAndClose('/settings/profile');
          }
        },
        ...(!this.isPublink ? [{
          primaryText: 'Security',
          isInteractive: true,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'shield'
          },
          onClick: () => {
            this.navigateAndClose('/settings/security');
          }
        },
        {
          primaryText: 'Get Sync apps',
          isInteractive: true,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'computer-and-phone'
          },
          onClick: () => {
            window.open('https://www.sync.com/install', '_blank');
            this.closePanel.emit();
            this.bottomSheet?.dismissBottomSheet?.();
          }
        },
        {
          primaryText: 'Change start page',
          isInteractive: true,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'flag-start'
          },
          onClick: async () => {
            this.changeStartPage.emit(true);
            this.bottomSheet?.dismissBottomSheet?.();
          }
        },
        {
          primaryText: 'Dark mode',
          isInteractive: true,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'mode-dark'
          },
          trail2AdornmentConfig: {
            enabled: true,
            type: 'switch',
            size: 'small',
            switchConfig: {
              size: 'default',
              checked: this.shareService.isDarkThemeSubject.getValue(),
            },
            onChange: async (toggleChange: any) => {
              this.shareService.setDarkTheme(toggleChange.checked);
              await this.userService.setUserInfo({ theme: toggleChange.checked ? 'dark' : 'light' });
              await this.userService.refresh();
            }
          },
          onClick: async () => {
            const checked = this.shareService.isDarkThemeSubject.getValue();
            this.shareService.setDarkTheme(!checked);
            this.profileMenuItems.find(item => item.primaryText == 'Dark Mode').trail2AdornmentConfig.switchConfig.checked = !checked;
            this.profileMenuItems = [...this.profileMenuItems];
            await this.userService.setUserInfo({ theme: !checked ? 'dark' : 'light' });
            await this.userService.refresh();
          }
        }] : [] as any ),
      ]

    this.signOutMenuItems = [
      {
        primaryText: 'Sign out',
        isInteractive: true,
        leadAdornmentConfig: {
          enabled: true,
          type: 'icon',
          size: 'large',
          iconName: 'sign-out'
        },
        onClick: () => {
          this.bottomSheet?.dismissBottomSheet?.();
          this.navigateAndClose('/logout');
        }
      }

    ]
    this.themeSubscription = this.shareService.isDarkTheme$.pipe(debounceTime(100)).subscribe(isDarkTheme => {
      const themeInd = this.profileMenuItems.findIndex((item)=>item.primaryText === 'Dark Theme');
      if(themeInd>-1){
        const profileMenuItems = [...this.profileMenuItems];
        const theme = profileMenuItems[themeInd];
        theme.trail2AdornmentConfig.switchConfig.checked = isDarkTheme;
        this.profileMenuItems = [...profileMenuItems];
      }
      if (isDarkTheme) {
        this.renderer.removeClass(document.body, 'light');
        this.renderer.addClass(document.body, 'dark');
      } else {
        this.renderer.removeClass(document.body, 'dark');
        this.renderer.addClass(document.body, 'light');
      }
    });

  }

  navigateAndClose(url: string) {
    if (this.isPublink) {
      // Hard redirect to cp for publink
      const redirectUrl = `${environment.syncCpHost}${url}`;
      window.location.href = redirectUrl;
    } else {
      // Normal app navigation
      this.router.navigate([url]).then(() => {
        this.closePanel.emit();
        this.bottomSheet?.dismissBottomSheet?.();
      });
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.themeSubscription){
      this.themeSubscription.unsubscribe();
    }
    if (this.broadcastSubscription){
      this.broadcastSubscription.unsubscribe();
    }
  }

  getProgressBarColor(value: number): string {
    let usageClass: string;
    if (value <= 39) {
      usageClass = 'low-storage-usage';
    } else if (value >= 40 && value <= 79) {
      usageClass = 'medium-storage-usage';
    } else {
      usageClass = 'high-storage-usage';
    }
    return 'storage-bar ' + usageClass;
  }

  onUpgradePlan() {
    this.blendService.track(BlendEvent.BUTTON_CLICKED, {button_text: 'Upgrade'});
    this.blendService.addEventsForUpgradeButton();
    this.chargebeeService.openPortal();
  };

}
